/* eslint-disable react-refresh/only-export-components */
import React, { createContext, useContext, ReactNode } from "react";
import { ErrorFactory } from "@core/lib/error-handling";

interface FeatureFlags {
  // Product features
  enableDatePolls: boolean;
  enableFormPolls: boolean;
  enableQuizz: boolean;

  // UI features
  enableDarkMode: boolean;
  enableAdvancedFilters: boolean;
  enableExportFeatures: boolean;

  // Beta features
  enableBetaFeatures: boolean;
  enableNewDashboard: boolean;

  // Analytics
  enableAnalytics: boolean;
  enableErrorTracking: boolean;

  // Feature Flags (Isolation of incomplete features)
  enableOneResponsePerPerson: boolean;
  enableVisibilityVotersOnly: boolean;
  enableVisibilityCreatorOnly: boolean;
  enableEnterprisePlan: boolean;
  enableUserDataEmailStatus: boolean;
  enableUserDataImproveToggle: boolean;
  enableMaybeVotes: boolean;
  enableEditAfterSubmit: boolean;
  /** Redesign polls V2: new dashboard card + vote + results modes (2026-04-22). */
  enableRedesignPollsV2: boolean;
  /** Pattern B "Settings Surfacing" : récap pré-publication avant `createPoll`. */
  enablePrePublishModal: boolean;
}

const defaultFlags: FeatureFlags = {
  enableDatePolls: true,
  enableFormPolls: true,
  enableQuizz: false, // Coming soon
  enableDarkMode: true,
  enableAdvancedFilters: true,
  enableExportFeatures: true,
  enableBetaFeatures: false,
  enableNewDashboard: false,
  enableAnalytics: true,
  enableErrorTracking: true,

  // Feature Flags (Isolation)
  enableOneResponsePerPerson: false,
  enableVisibilityVotersOnly: false,
  enableVisibilityCreatorOnly: false,
  enableEnterprisePlan: false,
  enableUserDataEmailStatus: false,
  enableUserDataImproveToggle: false,
  enableMaybeVotes: false,
  enableEditAfterSubmit: false,
  enableRedesignPollsV2: true,
  // Pre-publish modal (Pattern B). ON par défaut (activé 2026-05-22).
  //  - opt-out : env `VITE_ENABLE_PRE_PUBLISH_MODAL=false`
  //  - localStorage `doodates.enablePrePublishModal=false` (dev/E2E)
  enablePrePublishModal: (() => {
    if (typeof window === "undefined") return true;
    try {
      const lsVal = window.localStorage.getItem(
        "doodates.enablePrePublishModal",
      );
      if (lsVal === "false") return false;
    } catch {
      /* private mode */
    }
    const envFlag =
      typeof import.meta !== "undefined" &&
      import.meta.env?.VITE_ENABLE_PRE_PUBLISH_MODAL === "false";
    if (envFlag) return false;
    return true;
  })(),
};

interface FeatureFlagsContextType {
  flags: FeatureFlags;
  isEnabled: (feature: keyof FeatureFlags) => boolean;
  updateFlag: (feature: keyof FeatureFlags, value: boolean) => void;
}

const FeatureFlagsContext = createContext<FeatureFlagsContextType | undefined>(
  undefined,
);

interface FeatureFlagsProviderProps {
  children: ReactNode;
  initialFlags?: Partial<FeatureFlags>;
}

export const FeatureFlagsProvider: React.FC<FeatureFlagsProviderProps> = ({
  children,
  initialFlags = {},
}) => {
  const [flags, setFlags] = React.useState<FeatureFlags>({
    ...defaultFlags,
    ...initialFlags,
  });

  const isEnabled = (feature: keyof FeatureFlags): boolean => {
    return flags[feature] || false;
  };

  const updateFlag = (feature: keyof FeatureFlags, value: boolean) => {
    setFlags((prev) => ({ ...prev, [feature]: value }));
  };

  return (
    <FeatureFlagsContext.Provider value={{ flags, isEnabled, updateFlag }}>
      {children}
    </FeatureFlagsContext.Provider>
  );
};

export const useFeatureFlags = (): FeatureFlagsContextType => {
  const context = useContext(FeatureFlagsContext);
  if (context === undefined) {
    throw ErrorFactory.validation(
      "useFeatureFlags must be used within a FeatureFlagsProvider",
      "Erreur de configuration du contexte",
    );
  }
  return context;
};
